import { useState } from 'react'
import { Pagination } from '@/components/ui/Pagination'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getInbox } from '@/services/api'
import type { InboxItem } from '@/types/inbox'
import { workflowDetailQueryOptions } from '@/features/order-confirmation/workflowListQueries'
import { stripHtmlToText } from '@/components/EmailBody'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { OutlookFolderLabel } from '@/features/order-confirmation/inbox/OutlookFolderLabel'
import { outlookFolderName } from '@/features/order-confirmation/inbox/outlookFolder'
import { AttachmentChip } from '@/features/documents/AttachmentChip'

function formatDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (isToday) {
    return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
}

const MAX_CHIPS = 3

function InboxRow({
  item,
  onClick,
}: {
  item: InboxItem
  onClick: () => void
}) {
  const visibleChips = item.attachments.slice(0, MAX_CHIPS)
  const overflow = item.attachments.length - MAX_CHIPS

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="col-span-full grid grid-cols-subgrid items-start border-b border-gray-100 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
    >
      {/* Sender */}
      <div className="min-w-0 max-w-[20rem]">
        <div className="truncate text-sm font-semibold text-gray-900">
          {item.sender_address}
        </div>
      </div>

      {/* Subject + attachment chips */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-gray-700">
          {item.subject || <span className="italic text-gray-400">(no subject)</span>}
        </div>
        {item.attachments.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
            {visibleChips.map((a) => (
              <AttachmentChip key={a.id} name={a.name} />
            ))}
            {overflow > 0 && (
              <span className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-400">
                +{overflow}
              </span>
            )}
          </div>
        )}
        {item.body_preview && (
          <p className="mt-1 text-xs text-gray-400 truncate">
            {stripHtmlToText(item.body_preview)}
          </p>
        )}
      </div>

      {/* Folder */}
      <div className="max-w-[24rem] whitespace-nowrap text-xs text-gray-500 pt-0.5">
        {outlookFolderName(item.outlook_folder)
          ? <OutlookFolderLabel folder={item.outlook_folder} />
          : <span className="text-gray-300">—</span>}
      </div>

      {/* Date */}
      <div className="whitespace-nowrap text-right text-xs text-gray-400 pt-0.5">
        {formatDate(item.received)}
      </div>
    </div>
  )
}

export function InboxPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['inbox', workflowId, page],
    queryFn: () => getInbox(workflowId!, page),
    enabled: !!workflowId,
    ...workflowDetailQueryOptions,
  })

  const items = data?.data ?? []
  const isBackgroundRefreshing = isFetching && !isLoading && !!data

  if (isLoading) {
    return <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
  }

  return (
    <div className="relative container mx-auto">
      <ListRefreshIndicator visible={isBackgroundRefreshing} />
      <div className="overflow-hidden rounded-panel border border-canvas-border bg-surface p-3 shadow-panel">
        {/* One grid for header and rows (rows are subgrids), so the folder column is as wide
            as its longest path (10 to 24rem), the sender as its longest address (12 to 20rem), so
            they only move for unusually long values between pages; dates never wrap. */}
        <div className="data-sheet grid gap-x-3" style={{ gridTemplateColumns: 'minmax(12rem, max-content) minmax(0, 1fr) minmax(10rem, max-content) max-content' }}>
          {/* Column header */}
          <div className="col-span-full grid grid-cols-subgrid items-center border-b border-gray-200 bg-brand-100 px-4 py-2 select-none">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              From
            </div>
            <div className="min-w-0 flex-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Subject</div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Folder</div>
            <div className="text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">Date</div>
          </div>

          {items.length === 0 ? (
            <div className="col-span-full py-16 text-center text-sm text-gray-400">No emails in this inbox</div>
          ) : (
            items.map((item) => (
              <InboxRow
                key={item.id}
                item={item}
                onClick={() => navigate(`/workflow/${workflowId}/inbox/${item.id}`)}
              />
            ))
          )}
        </div>

        {data && (
          <Pagination
            page={data.page}
            totalCount={data.total}
            pageSize={data.page_size}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  )
}
